/**
 * Internationalisation — aucune chaîne destinée à l'utilisateur dans le code.
 *
 * Le français est la langue de lancement ; le wolof est prévu. **Ajouter une
 * langue ne demande aucune ligne de TypeScript** : on dépose un fichier à côté
 * de `fr.json` et la langue existe. Ce n'était pas vrai jusqu'ici — il fallait
 * aussi écrire un `import` et une entrée dans la table des ressources, c'est-à-
 * dire deux lignes de code, c'est-à-dire un développement. Les fichiers sont
 * désormais **découverts**, et la table est dérivée d'eux.
 *
 * i18next et non une solution maison : les pluriels, les formats de date et de
 * nombre, l'interpolation et le repli d'une langue sur l'autre sont exactement
 * ce qu'une solution maison réimplémente mal. Aucun service propriétaire n'est
 * requis (`AD-16`) — les traductions sont des fichiers du dépôt.
 *
 * **Le repli d'i18next n'est pas une excuse pour livrer une langue trouée.** Il
 * existe pour qu'une langue en cours de traduction reste activable ; le
 * contrôle `npm run check:translations` refuse, lui, qu'une langue incomplète
 * soit **livrée**. Les deux vont ensemble : sans le contrôle, le repli rendrait
 * un écran moitié wolof moitié français sans que personne ne le sache.
 */

import i18next from 'i18next';
import type { i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

/** La langue de lancement. Le wolof suivra, sans une ligne de code. */
export const defaultLocale = 'fr';

/** Le contenu d'un fichier de traduction, tel qu'un module JSON le rend. */
export interface TranslationModule {
  readonly default: Record<string, unknown>;
}

/** Une table de ressources i18next : une entrée par langue. */
export type TranslationResources = Record<string, { translation: Record<string, unknown> }>;

/**
 * Le code de langue porté par le chemin d'un fichier de traduction.
 *
 * `./locales/wo.json` donne `wo`. Le nom du fichier **est** le code : rien
 * d'autre à écrire, nulle part.
 */
export function localeCodeOf(path: string): string {
  const nom = path.split('/').pop() ?? path;
  return nom.replace(/\.json$/, '');
}

/**
 * Dérive la table des ressources des modules découverts.
 *
 * Séparée de la découverte elle-même pour être éprouvée : une fonction qui
 * traite n'importe quel code de langue de la même façon est ce qui rend
 * l'ajout d'une langue gratuit, et cela se vérifie sur une table écrite dans un
 * test.
 */
export function resourcesFrom(modules: Record<string, TranslationModule>): TranslationResources {
  const table: TranslationResources = {};
  for (const chemin of Object.keys(modules).sort()) {
    table[localeCodeOf(chemin)] = { translation: modules[chemin].default };
  }
  return table;
}

/**
 * Les langues livrées, la langue de lancement en tête puis les autres par
 * ordre alphabétique.
 *
 * L'ordre compte : c'est celui du repli. Le français, langue de lancement,
 * arrive donc toujours en premier.
 */
export function localesFrom(table: TranslationResources, fallback: string): string[] {
  const autres = Object.keys(table)
    .filter((code) => code !== fallback)
    .sort();
  return Object.hasOwn(table, fallback) ? [fallback, ...autres] : autres;
}

/**
 * Les fichiers de traduction du dépôt, découverts à la construction.
 *
 * `import.meta.glob` est résolu par Vite : déposer `wo.json` dans ce
 * répertoire suffit à ce que le module apparaisse ici, sans qu'aucune ligne ne
 * soit ajoutée. Le contrôle `check:translations` lit le **même répertoire sur
 * le disque** et refuse une langue incomplète ; un test compare les deux, sans
 * quoi la découverte pourrait s'arrêter sans que personne ne le voie.
 */
const modules = import.meta.glob<TranslationModule>('./locales/*.json', {
  eager: true,
});

/** Les ressources de traduction, une entrée par langue déposée. */
export const resources: TranslationResources = resourcesFrom(modules);

/** Les langues livrées, dans l'ordre de repli. */
export const supportedLocales: readonly string[] = localesFrom(resources, defaultLocale);

/**
 * Construit une instance d'i18next.
 *
 * Une **instance** et non le singleton du module : une suite de tests qui
 * partagerait l'instance de l'application verrait ses bascules de langue fuir
 * d'un test à l'autre.
 */
export function createI18n(locale: string = defaultLocale): I18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng: locale,
    fallbackLng: defaultLocale,
    // Pas de `supportedLngs` : une langue partiellement traduite doit pouvoir
    // être activée et **retomber clé par clé** sur le français. La borner ici
    // rendrait la bascule impossible tant que la traduction n'est pas
    // complète — c'est-à-dire au moment précis où elle sert. Ce que ce repli
    // ne doit jamais devenir, c'est une livraison silencieuse : voir
    // `tools/translation-coverage.ts`.
    interpolation: { escapeValue: false },
    // Une clé absente doit se voir en développement et ne jamais rendre une
    // chaîne vide : une étiquette vide se lit comme un défaut d'affichage,
    // pas comme un défaut de traduction.
    returnEmptyString: false,
  });
  return instance;
}
